import { useEffect, useRef, useState } from "react";
import { useTV } from "../../hooks/useTV";
import axios from "../../config/axios";
import useGlobalStore from "../../store/global.store";

const ParkBottomBar = ({ device, onBackClick }) => {
  const [focusedParkButton, setFocusedParkButton] = useState("parkHop");
  const [parksCount, setParksCount] = useState(0);

  useEffect(() => {
    console.log("Device changed");
  }, [device]);

  // Use the zustand store
  const {
    setShowParkChange,
    setShowRideChange,
    showParkChange,
    showRideChange,
  } = useGlobalStore();

  // Secondary bottom bar refs
  const parkHopButtonRef = useRef(null);
  const changeRideButtonRef = useRef(null);
  const backButtonRef = useRef(null);

  const { isTv } = useTV();

  const buttonStyle = isTv
    ? {
        marginLeft: "10px",
        border: "6px solid white",
        background: "transparent",
        color: "white",
        fontFamily: "Lato",
        fontSize: "55px",
        padding: "0px 20px",
        borderRadius: "20px",
        fontWeight: "700",
        userSelect: "none",
      }
    : {
        marginLeft: "10px",
        border: "2px solid white",
        background: "transparent",
        color: "white",
        fontFamily: "Lato",
        fontSize: "40px",
        padding: "0px 20px",
        borderRadius: "20px",
        fontWeight: "700",
        userSelect: "none",
      };

  // Check how many parks the user has access to
  useEffect(() => {
    axios
      .get(`/parks/public/${device._id}`, { withCredentials: true })
      .then((response) => {
        setParksCount(response.data.parks.length);
      })
      .catch((error) => {
        setParksCount(0);
      });
  }, [device._id]);

  // Auto-focus logic for TV
  useEffect(() => {
    if (isTv) {
      // Focus the park hop button by default if multiple parks available, otherwise focus change ride
      if (parksCount > 1 && parkHopButtonRef.current) {
        parkHopButtonRef.current.focus();
        setFocusedParkButton("parkHop");
      } else if (changeRideButtonRef.current) {
        changeRideButtonRef.current.focus();
        setFocusedParkButton("changeRide");
      }
    }
  }, [isTv, parksCount]);

  // Handle keyboard navigation
  useEffect(() => {
    if (isTv) {
      const handleKeyDown = (e) => {
        // Skip keyboard navigation if modal is open
        if (showParkChange || showRideChange) {
          return;
        }

        // Determine the available buttons based on parksCount
        const buttons =
          parksCount > 1
            ? ["parkHop", "changeRide", "back"]
            : ["changeRide", "back"];

        // Get current index
        const currentIndex = buttons.indexOf(focusedParkButton);

        if (e.key === "ArrowRight") {
          e.preventDefault();
          // Navigate to next button or wrap to first
          const nextIndex = (currentIndex + 1) % buttons.length;
          const nextButton = buttons[nextIndex];
          setFocusedParkButton(nextButton);

          // Focus the appropriate button
          if (nextButton === "parkHop" && parkHopButtonRef.current) {
            parkHopButtonRef.current.focus();
          } else if (
            nextButton === "changeRide" &&
            changeRideButtonRef.current
          ) {
            changeRideButtonRef.current.focus();
          } else if (nextButton === "back" && backButtonRef.current) {
            backButtonRef.current.focus();
          }
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          // Navigate to previous button or wrap to last
          const prevIndex =
            (currentIndex - 1 + buttons.length) % buttons.length;
          const prevButton = buttons[prevIndex];
          setFocusedParkButton(prevButton);

          // Focus the appropriate button
          if (prevButton === "parkHop" && parkHopButtonRef.current) {
            parkHopButtonRef.current.focus();
          } else if (
            prevButton === "changeRide" &&
            changeRideButtonRef.current
          ) {
            changeRideButtonRef.current.focus();
          } else if (prevButton === "back" && backButtonRef.current) {
            backButtonRef.current.focus();
          }
        }
      };

      // Add event listener
      window.addEventListener("keydown", handleKeyDown);

      // Clean up
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isTv, focusedParkButton, parksCount, showParkChange, showRideChange]);

  // Helper function to get park button styles
  const getParkButtonStyles = (buttonId, baseStyle) => {
    // Only apply focus styles for TV mode
    if (isTv && focusedParkButton === buttonId) {
      return {
        ...baseStyle,
        color: "white",
        borderColor: "#3ADF3C",
        outline: "none",
        border: "6px solid #3ADF3C",
      };
    }

    return {
      ...baseStyle,
      color: "white",
      borderColor: "white",
    };
  };

  // Event handlers for park bottom bar
  const handleParkHopClick = () => {
    console.log("PARK HOP clicked - Show available parks");
    // Open the park change modal
    if (parksCount > 1) {
      setShowParkChange(true);
    }
  };

  const handleChangeRideClick = () => {
    console.log("CHANGE RIDE clicked - Show ride list");
    // Open the ride change modal
    setShowRideChange(true);
  };

  const handleBackClick = () => {
    // Call the provided callback to go back to regular bottom bar
    onBackClick();
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "2.2%",
        display: "flex",
        justifyContent: "space-around",
        width: "100vw",
        zIndex: 110,
      }}
    >
      {parksCount > 1 && (
        <button
          id="parkHopButton"
          ref={parkHopButtonRef}
          onClick={handleParkHopClick}
          onContextMenu={(e) => e.preventDefault()}
          onFocus={() => setFocusedParkButton("parkHop")}
          onBlur={() => setFocusedParkButton(null)}
          tabIndex={showParkChange || showRideChange ? -1 : 0}
          style={getParkButtonStyles("parkHop", buttonStyle)}
        >
          PARK HOP
        </button>
      )}
      <button
        id="changeRideButton"
        ref={changeRideButtonRef}
        onClick={handleChangeRideClick}
        onContextMenu={(e) => e.preventDefault()}
        onFocus={() => setFocusedParkButton("changeRide")}
        onBlur={() => setFocusedParkButton(null)}
        tabIndex={showParkChange || showRideChange ? -1 : 0}
        style={getParkButtonStyles("changeRide", buttonStyle)}
      >
        CHANGE RIDE
      </button>
      <button
        ref={backButtonRef}
        onClick={handleBackClick}
        onContextMenu={(e) => e.preventDefault()}
        onFocus={() => setFocusedParkButton("back")}
        onBlur={() => setFocusedParkButton(null)}
        tabIndex={showParkChange || showRideChange ? -1 : 0}
        style={getParkButtonStyles("back", buttonStyle)}
      >
        BACK
      </button>
    </div>
  );
};

export default ParkBottomBar;
